import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { Capacitor } from '@capacitor/core';
import type { AuthUser, TenantInfo } from '@/types/api';
import { STORAGE_KEYS } from '@/constants';
import { hybridStorage } from '@/utils/storage';
import { ROLES } from '@/constants';
import { isFeatureEffective } from '@/permissions/societyFeatures';

const WILDCARD_PERMISSION = '*';

/** `foo.view`/`foo.read` and `foo.edit`/`foo.update` are interchangeable aliases. */
function aliasPermission(permission: string): string | null {
  const separatorIndex = permission.lastIndexOf('.');
  if (separatorIndex === -1) return null;
  const module = permission.slice(0, separatorIndex);
  const action = permission.slice(separatorIndex + 1);
  if (action === 'view') return `${module}.read`;
  if (action === 'read') return `${module}.view`;
  if (action === 'edit') return `${module}.update`;
  if (action === 'update') return `${module}.edit`;
  return null;
}

function matchesPermission(granted: string[], required: string): boolean {
  if (granted.includes(required)) return true;
  const alias = aliasPermission(required);
  return alias ? granted.includes(alias) : false;
}

export type AuthMachineState =
  | 'AUTH_BOOTSTRAPPING'
  | 'AUTHENTICATED'
  | 'BIOMETRIC_REQUIRED'
  | 'UNAUTHENTICATED'
  | 'AUTHENTICATION_FAILED';

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  tenant: TenantInfo | null;
  permissions: string[];
  societyFeatures: Record<string, boolean>;
  rememberMe: boolean;
  hydrated: boolean;
  authState: AuthMachineState;
  isBiometricLocked: boolean;
  setHydrated: (value: boolean) => void;
  setAuthState: (state: AuthMachineState) => void;
  setBiometricLocked: (locked: boolean) => void;
  setSession: (payload: {
    user: AuthUser;
    accessToken: string;
    refreshToken: string;
    tenant?: TenantInfo | null;
    permissions?: string[];
    features?: Record<string, boolean>;
    societyFeatures?: Record<string, boolean>;
    rememberMe?: boolean;
  }) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: AuthUser | null) => void;
  setTenant: (tenant: TenantInfo | null) => void;
  setSocietyFeatures: (features: Record<string, boolean>) => void;
  setRememberMe: (value: boolean) => void;
  logout: () => void;
  isAuthenticated: () => boolean;
  hasRole: (role: string | string[]) => boolean;
  isSuperAdmin: () => boolean;
  /** True only if every requested permission is granted (with view/read aliasing). */
  hasPermission: (permission: string | string[]) => boolean;
  /** True if at least one requested permission is granted (with view/read aliasing). */
  hasAnyPermission: (permission: string[]) => boolean;
  /** True if society has the requested feature enabled (Super Admin has all features). */
  hasFeature: (featureKey: string) => boolean;
  /** Alias for hasPermission; always true for SUPER_ADMIN or the '*' wildcard permission. */
  can: (permission: string | string[]) => boolean;
  cannot: (permission: string | string[]) => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      tenant: null,
      permissions: [],
      societyFeatures: {},
      rememberMe: true,
      hydrated: false,
      authState: 'AUTH_BOOTSTRAPPING',
      isBiometricLocked: true,
      setHydrated: (value) => set({ hydrated: value }),
      setAuthState: (authState) => set({ authState }),
      setBiometricLocked: (isBiometricLocked) => set({ isBiometricLocked }),
      setSession: ({ user, accessToken, refreshToken, tenant, permissions, features, societyFeatures, rememberMe }) =>
        set({
          user,
          accessToken,
          refreshToken,
          tenant: tenant ?? null,
          permissions: permissions ?? user.permissions ?? [],
          societyFeatures: societyFeatures ?? features ?? (user as any).societyFeatures ?? (user as any).features ?? {},
          rememberMe: rememberMe ?? get().rememberMe,
          authState: 'AUTHENTICATED',
          isBiometricLocked: false,
        }),
      setTokens: (accessToken, refreshToken) => set({ accessToken, refreshToken }),
      setUser: (user) => set({
        user,
        societyFeatures: (user as any)?.societyFeatures ?? (user as any)?.features ?? get().societyFeatures,
      }),
      setTenant: (tenant) => set({ tenant }),
      setSocietyFeatures: (societyFeatures) => set({ societyFeatures }),
      setRememberMe: (rememberMe) => set({ rememberMe }),
      logout: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          tenant: null,
          permissions: [],
          societyFeatures: {},
          authState: 'UNAUTHENTICATED',
          isBiometricLocked: false,
        }),
      isAuthenticated: () =>
        Boolean(
          get().accessToken &&
            get().user &&
            !get().isBiometricLocked &&
            get().authState === 'AUTHENTICATED',
        ),
      hasRole: (role) => {
        const { user } = get();
        if (!user?.roles?.length) return false;
        const needed = Array.isArray(role) ? role : [role];
        return needed.some((r) => user.roles.includes(r));
      },
      isSuperAdmin: () => get().hasRole(ROLES.SUPER_ADMIN) || (get().user as any)?.role === 'SUPER_ADMIN' || (get().user as any)?.scope === 'PLATFORM',
      hasFeature: (featureKey) => {
        if (get().isSuperAdmin()) return true;
        const features = get().societyFeatures;
        return isFeatureEffective(features, featureKey);
      },
      hasPermission: (permission) => {
        if (get().isSuperAdmin() || get().hasRole(ROLES.PRESIDENT)) return true;
        const granted = get().permissions;
        const required = Array.isArray(permission) ? permission : [permission];
        if (!required.length) return true;
        return required.every((p) => matchesPermission(granted, p));
      },
      hasAnyPermission: (permission) => {
        if (get().isSuperAdmin() || get().hasRole(ROLES.PRESIDENT)) return true;
        const granted = get().permissions;
        if (!permission.length) return false;
        return permission.some((p) => matchesPermission(granted, p));
      },
      can: (permission) => {
        if (get().isSuperAdmin()) return true;
        if (get().hasRole(ROLES.PRESIDENT)) return true;
        if (get().permissions.includes(WILDCARD_PERMISSION)) return true;
        return get().hasPermission(permission);
      },
      cannot: (permission) => !get().can(permission),
    }),
    {
      name: STORAGE_KEYS.auth,
      storage: createJSONStorage(() => hybridStorage),
      partialize: (state) => {
        if (!state.rememberMe) {
          return {
            rememberMe: state.rememberMe,
            user: null,
            accessToken: null,
            refreshToken: null,
            tenant: null,
            permissions: [],
            societyFeatures: {},
          };
        }
        return {
          user: state.user,
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          tenant: state.tenant,
          permissions: state.permissions,
          societyFeatures: state.societyFeatures,
          rememberMe: state.rememberMe,
        };
      },
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        // On native mobile (Android/iOS), do NOT assume session is authenticated during sync hydration.
        // Keep in AUTH_BOOTSTRAPPING / isBiometricLocked=true until bootstrapAuthLifecycle() evaluates native storage.
        if (Capacitor.isNativePlatform()) {
          state.setAuthState('AUTH_BOOTSTRAPPING');
          state.setBiometricLocked(true);
          return;
        }

        // On Web browser, restore session state immediately
        try {
          if (state.accessToken && state.user) {
            state.setBiometricLocked(false);
            state.setAuthState('AUTHENTICATED');
          } else {
            state.setBiometricLocked(false);
            state.setAuthState('UNAUTHENTICATED');
          }
        } finally {
          state.setHydrated(true);
        }
      },
    },
  ),
);
